import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import '../../styles/Dashboard.css';

const EmployeeNotificationsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      // Always fetch ALL notifications (no filter) to get accurate counts
      const response = await api.getNotifications({ filter: 'all' });
      
      if (response.success) {
        // Transform backend notifications to match frontend format
        const transformedNotifications = response.data.map(notif => ({
          id: notif._id || notif.id,
          type: notif.type,
          title: notif.title,
          message: notif.message,
          priority: notif.priority,
          actionUrl: notif.actionUrl,
          date: notif.createdAt,
          read: notif.read,
          readAt: notif.readAt,
          handover: notif.handover,
          asset: notif.asset,
          assetHistory: notif.assetHistory,
        }));
        
        setNotifications(transformedNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      // Use the MongoDB _id if available, otherwise use id
      const idToUse = String(notificationId);
      const response = await api.markNotificationAsRead(idToUse);
      
      if (response.success) {
        // Refresh notifications from backend to get updated state
        await fetchNotifications();
        
        // Notify Topbar to refresh
        window.dispatchEvent(new Event('notificationsUpdated'));
      } else {
        console.error('Failed to mark notification as read:', response.message || response);
        alert('Failed to mark notification as read. Please try again.');
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      alert('Error marking notification as read. Please try again.');
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await api.markAllNotificationsAsRead();
      
      if (response.success) {
        // Refresh notifications from backend to get updated state
        await fetchNotifications();
        
        // Notify Topbar to refresh
        window.dispatchEvent(new Event('notificationsUpdated'));
      } else {
        console.error('Failed to mark all notifications as read:', response.message);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      // Use the MongoDB _id if available, otherwise use id
      const idToUse = String(notificationId);
      const response = await api.deleteNotification(idToUse);
      
      if (response.success) {
        // Refresh notifications from backend to get updated state
        await fetchNotifications();
        
        // Notify Topbar to refresh
        window.dispatchEvent(new Event('notificationsUpdated'));
      } else {
        console.error('Failed to delete notification:', response.message || response);
        alert('Failed to delete notification. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Error deleting notification. Please try again.');
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate to action URL if available
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const filteredNotifications = notifications.filter((notif) => {
    if (filter === 'unread') return !notif.read;
    if (filter === 'read') return notif.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading notifications...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Notifications</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '13px',
              }}
            >
              Mark All as Read
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', borderBottom: '1px solid #e2e8f0' }}>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '10px 20px',
            background: 'none',
            border: 'none',
            borderBottom: filter === 'all' ? '2px solid #667eea' : '2px solid transparent',
            color: filter === 'all' ? '#667eea' : '#718096',
            cursor: 'pointer',
            fontWeight: filter === 'all' ? 600 : 400,
            fontSize: '14px',
          }}
        >
          All ({notifications.length})
        </button>
        <button
          onClick={() => setFilter('unread')}
          style={{
            padding: '10px 20px',
            background: 'none',
            border: 'none',
            borderBottom: filter === 'unread' ? '2px solid #667eea' : '2px solid transparent',
            color: filter === 'unread' ? '#667eea' : '#718096',
            cursor: 'pointer',
            fontWeight: filter === 'unread' ? 600 : 400,
            fontSize: '14px',
          }}
        >
          Unread ({unreadCount})
        </button>
        <button
          onClick={() => setFilter('read')}
          style={{
            padding: '10px 20px',
            background: 'none',
            border: 'none',
            borderBottom: filter === 'read' ? '2px solid #667eea' : '2px solid transparent',
            color: filter === 'read' ? '#667eea' : '#718096',
            cursor: 'pointer',
            fontWeight: filter === 'read' ? 600 : 400,
            fontSize: '14px',
          }}
        >
          Read ({notifications.filter(n => n.read).length})
        </button>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#718096',
          background: '#f7fafc',
          borderRadius: '8px',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔔</div>
          <div style={{ fontSize: '16px', fontWeight: 500 }}>
            {filter === 'unread' ? 'No unread notifications' : filter === 'read' ? 'No read notifications' : 'No notifications'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              style={{
                padding: '16px',
                background: notification.read ? '#ffffff' : '#f0f4ff',
                border: `1px solid ${notification.read ? '#e2e8f0' : '#cbd5e0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = notification.read ? '#f7fafc' : '#e6edff';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = notification.read ? '#ffffff' : '#f0f4ff';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    {!notification.read && (
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#667eea',
                        display: 'inline-block',
                      }} />
                    )}
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '16px', 
                      fontWeight: notification.read ? 500 : 600,
                      color: notification.read ? '#4a5568' : '#2d3748',
                    }}>
                      {notification.title}
                    </h3>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      background: notification.priority === 'high' ? '#fed7d7' : notification.priority === 'medium' ? '#feebc8' : '#c6f6d5',
                      color: notification.priority === 'high' ? '#c53030' : notification.priority === 'medium' ? '#c05621' : '#22543d',
                    }}>
                      {notification.priority}
                    </span>
                  </div>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '14px', 
                    color: '#718096',
                    lineHeight: '1.5',
                  }}>
                    {notification.message}
                  </p>
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '12px', 
                    color: '#a0aec0',
                  }}>
                    {new Date(notification.date).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                  {!notification.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#e2e8f0',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#4a5568',
                      }}
                      title="Mark as read"
                    >
                      Mark Read
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this notification?')) {
                        deleteNotification(notification.id);
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      background: '#fed7d7',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#c53030',
                    }}
                    title="Delete notification"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeeNotificationsPage;
